import { PERSONAS } from '@modules/aawz-intelligence-v2/constants/Personas'
import {
  GRUPO_SEM_PASTA,
  ICONE_GRUPO_PASTA,
  MODO_AGRUPAMENTO_AGENTE,
  MODO_AGRUPAMENTO_PASTAS,
} from '@modules/aawz-intelligence-v2/constants/TextChat'
import {
  HUBS,
  HUB_GESTAO,
  HUB_PROFISSIONAL,
  HUB_SERVICOS,
  getHub,
} from '@modules/aawz-intelligence-v2/constants/Hubs'

/**
 * Agrupa as conversas da sidebar num dos 4 modos de "Agrupar por" (§10).
 *
 * Puro e fora de `mutations.js`: agrupar é derivação do que já está no store,
 * e o único jeito de provar as regras de ordem é chamar com lista literal.
 *
 * Grupo = `{ chave, titulo, iconName, folder, itens }`. `folder` não estava no
 * contrato do plano: entrou porque o modo Pastas reusa `folder-item.vue` como
 * drop-target (AC-5.4), que exige o registro inteiro da pasta. É `null` em
 * qualquer grupo que não nasce de uma pasta.
 */

// String dos dois lados: `id_folder` e `id` chegam de chamadas diferentes, uma
// pode vir number e a outra string sem que nada avise.
function mesmaPasta(conversa, pastaId) {
  const atual = conversa && conversa.id_folder
  return Boolean(atual) && String(atual) === String(pastaId)
}

function semPasta(conversa) {
  return !(conversa && conversa.id_folder)
}

function montarGrupo(chave, titulo, iconName, itens, folder) {
  return { chave, titulo, iconName, itens, folder: folder || null }
}

// Pasta vazia CONTINUA na lista: é o alvo do drag-and-drop, e sumir ao esvaziar
// deixaria o usuário sem para onde arrastar de volta (igual conversations.vue).
function agruparPorPasta(conversas, pastas) {
  const grupos = pastas.map((pasta) =>
    montarGrupo(
      'pasta-' + pasta.id,
      pasta.name,
      ICONE_GRUPO_PASTA,
      conversas.filter((conversa) => mesmaPasta(conversa, pasta.id)),
      pasta
    )
  )
  // "Sem pasta" sempre por último (AC-5.4).
  const soltas = conversas.filter(semPasta)
  return grupos.concat(
    montarGrupo('sem-pasta', GRUPO_SEM_PASTA, ICONE_GRUPO_PASTA, soltas)
  )
}

/**
 * Rótulo canônico de um grupo de conversas — o nome da PERSONA.
 *
 * ★ O agrupamento é por `conversation.context`, que guarda a PERSONA e não o
 * agente: Painel e Consultor apontam para a MESMA (`CONSULTOR_ESTRATEGICO`), e
 * no dado eles são um grupo só. O nome tem de vir da persona, não de um agente
 * qualquer que aponte para ela — senão acrescentar uma quinta entrada ao
 * catálogo renomearia o grupo em silêncio, contra a promessa data-driven do
 * AC-4.5. Hoje a string é a mesma ('Consultor Estratégico'), mas deixa de
 * depender da ordem.
 *
 * Fallback no rótulo do agente: uma persona local (resolvida por getter do
 * próprio módulo) pode não estar em `PERSONAS`, e ficar sem nome é pior.
 *
 * @param {String} persona Valor de `context`.
 * @param {Object} agente Agente do catálogo que aponta para ela.
 * @returns {String} Rótulo do grupo.
 */
export function rotuloDaPersona(persona, agente) {
  if (persona && Object.hasOwn(PERSONAS, persona)) {
    return PERSONAS[persona].label
  }
  return (agente && agente.label) || ''
}

/**
 * Índice `persona -> rótulo do grupo`, para quem precisa traduzir `context` em
 * texto legível linha a linha (a lista de Recentes e o modo Pastas).
 *
 * Montado uma vez pelo consumidor e não uma busca por linha renderizada.
 *
 * @param {Array} agentes Catálogo dos agentes visíveis.
 * @returns {Object} Mapa `persona -> rótulo`.
 */
export function indexarRotulosPorPersona(agentes) {
  const indice = {}
  for (const agente of agentes || []) {
    indice[agente.persona] = rotuloDaPersona(agente.persona, agente)
  }
  return indice
}

/**
 * Espelho FRONT de `MAPA_TRANSICAO_LEGADO` (`constants/hubs.py`, backend) —
 * os valores de PERSONA que `conversa.context` ainda carrega até a
 * migration `v643` rodar (spec-010 [T023]/UC-6). Mora AQUI, e não em
 * `constants/Hubs.js` ([T025], já fechado com o próprio drift-guard): o
 * PROMPT-BASE desta spec pede parar e reportar em vez de editar arquivo
 * alheio — a alternativa aceita é um mapa PRÓPRIO com drift-guard PRÓPRIO
 * (ver describe "drift contra constants/hubs.py" em `agrupar-conversas.unit.js`).
 *
 * Redigitado por VALOR de propósito, como o próprio backend faz (docstring de
 * `hubs.py`): este mapa espelha os valores LEGADOS da coluna, não o catálogo
 * `PERSONAS` — que continua importado no topo para `rotuloDaPersona`.
 *
 * `export`ado para `conversation-row.vue` — que precisa saber se um
 * `context` é um dos valores LEGADOS (e só então mostrar o nome do último
 * agente no chip) sem reimplementar a lista ao lado.
 */
export const MAPA_TRANSICAO_LEGADO_HUB = {
  CRM: HUB_PROFISSIONAL,
  ESPECIALISTA_FEE_FIXO: HUB_PROFISSIONAL,
  // ★ [19/09/2026] Espelha a linha que o backend acrescentou por HIGIENE: o
  // Especialista em Comissionamento é posterior à migration `v643`, então não
  // há `context` legado dele para converter. Aqui o valor COINCIDE com o
  // default (`HUB_PROFISSIONAL`), e por isso a ausência não mudava nada na
  // tela — mas era o mapa e o default concordando por acaso. Declarado, a
  // conversa de comissões continua no hub certo mesmo se o default mudar.
  ESPECIALISTA_COMISSOES: HUB_PROFISSIONAL,
  CONSULTOR_ESTRATEGICO: HUB_GESTAO,
}

/**
 * `Hub` D6 do valor (novo OU legado) de `conversa.context` — espelho FRONT de
 * `hub_da_conversa` (`constants/hubs.py::hub_da_conversa`, UC-6). Idempotente
 * para um id de hub já válido; os 3 valores de persona legados resolvem pelo
 * mapa de transição; qualquer outro valor (`AI_FRAMEWORK`, vazio, lixo) cai
 * no default (Hub do Profissional, mesmo `HUB_DEFAULT` do backend) — NUNCA
 * lança.
 *
 * @param {String} context Valor persistido de `conversa.context`.
 * @returns {String} Um dos `HUB_*`.
 */
export function hubDaConversa(context) {
  if (context && Object.hasOwn(HUBS, context)) return context
  if (context && Object.hasOwn(MAPA_TRANSICAO_LEGADO_HUB, context)) {
    return MAPA_TRANSICAO_LEGADO_HUB[context]
  }
  return HUB_PROFISSIONAL
}

// Ordem canônica de `Hubs.js` — a mesma nos 3 grupos, sempre, mesmo vazios: ao
// contrário de pasta/agente (que crescem/somem), o conjunto de hubs é FIXO, e
// um grupo que sumisse ao esvaziar faria a lista "pular" de tamanho a cada
// troca de conversa.
const ORDEM_HUBS = [HUB_PROFISSIONAL, HUB_GESTAO, HUB_SERVICOS]

/**
 * Agrupa por HUB (D6) — spec-010 [T029] US-7: "a listagem lateral agrupa
 * conversas por hub, não por persona". Substitui o antigo agrupamento por
 * agente/persona (que dependia do catálogo `agentes` e inventava um grupo
 * "Outros" para `context` órfão); como qualquer valor de `context` resolve para
 * um dos 3 hubs (`hubDaConversa` nunca devolve "desconhecido"), não há mais
 * órfão a acomodar.
 *
 * @param {Array} conversas
 * @returns {Array} Sempre 3 grupos, na ordem `ORDEM_HUBS`.
 */
function agruparPorHub(conversas) {
  const porHub = {}
  for (const hubId of ORDEM_HUBS) porHub[hubId] = []
  for (const conversa of conversas || []) {
    const hubId = hubDaConversa(conversa && conversa.context)
    porHub[hubId].push(conversa)
  }
  return ORDEM_HUBS.map(function (hubId) {
    const hub = getHub(hubId)
    return montarGrupo('hub-' + hubId, hub.label, hub.iconName, porHub[hubId])
  })
}

/**
 * @param {Array} conversas Conversas carregadas (já filtradas pela busca).
 * @param {String} modo Um dos `MODO_AGRUPAMENTO_*`.
 * @param {Object} contexto `{ pastas }` — só o modo Pastas precisa de catálogo;
 *   o modo por hub resolve pelo `context` da própria conversa.
 * @returns {Array} `[{ chave, titulo, iconName, folder, itens }]`.
 */
export function agruparConversas(conversas, modo, { pastas } = {}) {
  const lista = conversas || []
  if (modo === MODO_AGRUPAMENTO_PASTAS) {
    return agruparPorPasta(lista, pastas || [])
  }
  if (modo === MODO_AGRUPAMENTO_AGENTE) {
    return agruparPorHub(lista)
  }
  // Profissional (G6) e Cliente (G7) não têm dado no payload da conversa.
  // Lista vazia — o botão do modo já chega `disabled`, e nada é inventado.
  return []
}

// ---------------------------------------------------------------------------
// Helpers da seção RECENTES e da busca (spec-007 R7, do #1961).
//
// Vieram do merge com o `desenvolvimento`. Os nossos componentes faziam o mesmo
// inline; passaram a chamar daqui porque a convenção do repo é helper puro em
// `utils/` — e porque o `filtrarPorTitulo` normaliza acento, coisa que a nossa
// busca inline não fazia: "captacao" agora encontra "Captação".
// ---------------------------------------------------------------------------

// Teto da lista plana de RECENTES antes do "Ver todas (N)" — o desenho do
// protótipo v0. Acima disso a seção deixa de ser atalho e vira a lista inteira.
export const RECENTES_LIMITE = 5

/**
 * As conversas mais recentes, com teto e o material do botão "Ver todas".
 *
 * A lista já chega ordenada do backend (mais recente primeiro) — aqui só se
 * fatia. `total` é o tamanho cheio para o rótulo "Ver todas (N)".
 *
 * @param {Array} conversas Lista completa, já ordenada.
 * @param {Boolean} expandido `true` depois do clique em "Ver todas".
 * @returns {{visiveis: Array, total: Number, temMais: Boolean}}
 */
export function limitarRecentes(conversas, expandido) {
  const todas = conversas || []
  const temMais = todas.length > RECENTES_LIMITE
  return {
    visiveis: expandido ? todas.slice() : todas.slice(0, RECENTES_LIMITE),
    total: todas.length,
    temMais: temMais,
  }
}

/**
 * Normaliza para busca: sem acento (NFD + strip de combining marks), minúsculas.
 * "captacao" encontra "Captação" — o gestor não vai lembrar do acento.
 */
function normalizado(texto) {
  return String(texto || '')
    .normalize('NFD')
    .replaceAll(/[̀-ͯ]/g, '')
    .toLowerCase()
}

/**
 * Filtra conversas pelo título (`topic`), sem acento e sem caixa.
 * Termo vazio devolve a lista inteira (cópia) — a busca "desligada".
 *
 * @param {Array} conversas
 * @param {String} termo O que o gestor digitou.
 * @returns {Array}
 */
export function filtrarPorTitulo(conversas, termo) {
  const alvo = normalizado(termo).trim()
  if (!alvo) {
    return (conversas || []).slice()
  }
  return (conversas || []).filter((conversa) =>
    normalizado(conversa.topic).includes(alvo))
}
